/**
 * Rate Limiter for AI Admin Commands
 * Implements a sliding window rate limiter to prevent abuse
 */

import { RateLimitState } from './types';

// Store rate limit state per user (in-memory for now)
// In production, this should be Redis or similar
const rateLimitStore = new Map<string, RateLimitState>();

const DEFAULT_LIMIT = 30; // requests
const DEFAULT_WINDOW_MS = 60 * 1000; // 1 minute

export function checkRateLimit(
  userId: string,
  limit: number = DEFAULT_LIMIT,
  windowMs: number = DEFAULT_WINDOW_MS
): { allowed: boolean; remaining: number; resetAt: Date } {
  const now = Date.now();
  
  // Get or create rate limit state for this user
  let state = rateLimitStore.get(userId);
  
  if (!state) {
    state = {
      requests: [],
      limit,
      windowMs,
    };
    rateLimitStore.set(userId, state);
  }

  // Remove requests outside the current window
  state.requests = state.requests.filter(timestamp => timestamp > now - windowMs);

  // Check if limit is exceeded
  const allowed = state.requests.length < limit;
  
  if (allowed) {
    // Add this request to the window
    state.requests.push(now);
  }

  // Calculate when the rate limit resets (when oldest request expires)
  const oldestRequest = state.requests[0] || now;
  const resetAt = new Date(oldestRequest + windowMs);

  return {
    allowed,
    remaining: Math.max(0, limit - state.requests.length),
    resetAt,
  };
}

export function getRateLimitStatus(userId: string): {
  used: number;
  remaining: number;
  limit: number;
} {
  const state = rateLimitStore.get(userId);
  
  if (!state) {
    return {
      used: 0,
      remaining: DEFAULT_LIMIT,
      limit: DEFAULT_LIMIT,
    };
  }

  const now = Date.now();
  const validRequests = state.requests.filter(
    timestamp => timestamp > now - state.windowMs
  );

  return {
    used: validRequests.length,
    remaining: Math.max(0, state.limit - validRequests.length),
    limit: state.limit,
  };
}

// Clean up old rate limit entries periodically
setInterval(() => {
  const now = Date.now();
  for (const [userId, state] of rateLimitStore.entries()) {
    // Remove requests outside the window
    state.requests = state.requests.filter(
      timestamp => timestamp > now - state.windowMs
    );
    
    // Remove user entry if no recent requests
    if (state.requests.length === 0) {
      rateLimitStore.delete(userId);
    }
  }
}, 60 * 1000); // Clean up every minute
